import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { WhipLogo } from "@/components/WhipLogo";
import About from "@/pages/About";
import Login from "@/pages/Login";
import Privacy from "@/pages/Privacy";
import AdminSystem from "@/pages/AdminSystem";
import ForecastBacktest from "@/pages/ForecastBacktest";
import { GlobalTooltip } from "@/components/GlobalTooltip";
import AdminUsers from "@/pages/AdminUsers";
import LandingDashboard from "@/pages/LandingDashboard";
import Dashboard from "@/pages/Dashboard";
import BillDetail from "@/pages/BillDetail";
import Legislators from "@/pages/Legislators";
import LegislatorProfile from "@/pages/LegislatorProfile";
import Compare from "@/pages/Compare";
import Rankings from "@/pages/Rankings";
import Factions from "@/pages/Factions";
import Committees from "@/pages/Committees";
import CommitteeDetail from "@/pages/CommitteeDetail";
import Parties from "@/pages/Parties";
import PartyDetail from "@/pages/PartyDetail";
import Signals from "@/pages/Signals";
import Radar from "@/pages/Radar";
import Watchlist from "@/pages/Watchlist";
import SetPassword from "@/pages/SetPassword";
import SetupPassword from "@/pages/SetupPassword";
import RequestSetupLink from "@/pages/RequestSetupLink";
import { usePageTitle } from "@/hooks/usePageTitle";
import { FeatureRoute } from "@/components/entitlements/LockedPanel";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

const AuthSplash = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
    <WhipLogo size={32} />
    <p className="text-white/40 text-[11px] font-mono uppercase tracking-widest">Loading…</p>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  const location = useLocation();
  if (checking) return <AuthSplash />;
  if (!user) return <Navigate to="/about" replace />;
  // Temporary-password accounts must set a real password before using the app.
  if (user.must_change_password && location.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  usePageTitle();
  // Check URL fragment (not query params) for session_id, must run before routing.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/welcome/:token" element={<SetupPassword />} />
      <Route path="/setup-link" element={<RequestSetupLink />} />
      {/* Protected app */}
      <Route path="/set-password" element={<ProtectedRoute><SetPassword /></ProtectedRoute>} />
      <Route path="/account/password" element={<ProtectedRoute><SetPassword voluntary /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><LandingDashboard /></ProtectedRoute>} />
      <Route path="/legislation" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/signals" element={<ProtectedRoute><FeatureRoute feature="live_signals" tier="pro" title="Live Signals" blurb="Real-time momentum, surprise votes and floor-schedule signals across Congress, refreshed as filings and schedules publish."><Signals /></FeatureRoute></ProtectedRoute>} />
      <Route path="/accuracy" element={<ProtectedRoute><ForecastBacktest /></ProtectedRoute>} />
      <Route path="/watchlist" element={<ProtectedRoute><FeatureRoute feature="watchlists" tier="pro" title="Watchlist" blurb="Save bills, legislators, and Radar topics to track them in one place, and share them with your team on WHIP Intelligence."><Watchlist /></FeatureRoute></ProtectedRoute>} />
      <Route path="/radar" element={<ProtectedRoute><FeatureRoute feature="ai_radar" tier="intelligence" title="AI Radar" blurb="Sector-specific intelligence briefs, bills to watch with passage probabilities, legislators to watch with stance dossiers, and emerging sector themes for your industry."><Radar /></FeatureRoute></ProtectedRoute>} />
      <Route path="/bill/:billId" element={<ProtectedRoute><BillDetail /></ProtectedRoute>} />
      <Route path="/legislators" element={<ProtectedRoute><Legislators /></ProtectedRoute>} />
      <Route path="/legislator/:legislatorId" element={<ProtectedRoute><LegislatorProfile /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><FeatureRoute feature="compare" tier="pro" title="Compare Legislators" blurb="Side-by-side comparison of voting records, effectiveness, attendance, and bipartisanship."><Compare /></FeatureRoute></ProtectedRoute>} />
      <Route path="/rankings" element={<ProtectedRoute><FeatureRoute feature="legislator_rankings" tier="pro" title="Rankings & Scorecards" blurb="Legislator and legislation leaderboards, one-click personas, and multi-category Fit Scorecards."><Rankings /></FeatureRoute></ProtectedRoute>} />
      <Route path="/factions" element={<ProtectedRoute><FeatureRoute feature="alliance_map" tier="intelligence" title="Backroom" blurb="Interactive legislator network intelligence, voting alignment, co-sponsorship, and committee networks with AI-labeled communities and centrality analysis."><Factions /></FeatureRoute></ProtectedRoute>} />
      <Route path="/committees" element={<ProtectedRoute><Committees /></ProtectedRoute>} />
      <Route path="/committees/:committeeName" element={<ProtectedRoute><CommitteeDetail /></ProtectedRoute>} />
      <Route path="/parties" element={<ProtectedRoute><Parties /></ProtectedRoute>} />
      <Route path="/parties/:party" element={<ProtectedRoute><PartyDetail /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/system" element={<ProtectedRoute><AdminSystem /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors closeButton theme="dark" data-testid="app-toaster" />
      <GlobalTooltip />
    </div>
  );
}

export default App;
